/**
 * EG Search (Meilisearch) – results API.
 *
 * Builds query params and fetches product results for the /search page.
 */
window.EGSearchResultsApi = (function () {
  'use strict';

  const config = window.EGSearchConfig;
  const productMetadataCache = new Map();

  // Facet fields we ask the API for (drives what can show in the sidebar).
  // We intentionally leave out tags + vendor. Add new fields here once they're live in the index.
  const FACETS_LIST = 'product_type,color,organic,bloom_period,classification,growing_zones,light_requirement,height,type,plant_life_cycle,plant_characteristics,raised_bed_height';
  const HITS_PER_PAGE = 48;
  const EXCLUDED_PRODUCT_TYPES = [];

  function getStorefrontAccessToken() {
    try {
      const featuresElement = document.getElementById('shopify-features');
      if (!featuresElement) return '';
      const features = JSON.parse(featuresElement.textContent || '{}');
      return String(features?.accessToken || '').trim();
    } catch (_) {
      return '';
    }
  }

  function toProductGid(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (raw.startsWith('gid://shopify/Product/')) return raw;
    const numericId = raw.match(/\d+/)?.[0];
    return numericId ? `gid://shopify/Product/${numericId}` : '';
  }

  function numericIdFromGid(value) {
    const id = Number(String(value || '').split('/').pop());
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function richTextToPlainText(value) {
    if (!value) return '';

    try {
      const parsed = JSON.parse(value);
      const paragraphs = [];
      const walk = (node, current = []) => {
        if (!node || typeof node !== 'object') return;
        if (typeof node.value === 'string') current.push(node.value);
        const children = Array.isArray(node.children) ? node.children : [];
        children.forEach((child) => walk(child, current));
        if (node.type === 'paragraph' && current.length) {
          paragraphs.push(current.join('').trim());
          current.length = 0;
        }
      };
      const looseText = [];
      walk(parsed, looseText);
      if (looseText.length) paragraphs.push(looseText.join('').trim());
      return paragraphs.filter(Boolean).join(' ');
    } catch (_) {
      return String(value).trim();
    }
  }

  function normalizeMetadataProduct(product) {
    if (!product) return null;
    const preorderEnabled = String(product.preorderItem?.value || '').toLowerCase() === 'true';
    const preorderTimeline = richTextToPlainText(product.preorderTimeline?.value || '');
    const hasAnyVariantConfig = Array.isArray(product.variants?.nodes)
      && product.variants.nodes.some((variant) => variant?.preorderItem != null || variant?.preorderTimeline != null);
    const variantPreorderStates = Array.isArray(product.variants?.nodes)
      ? product.variants.nodes.reduce((states, variant) => {
        const variantId = numericIdFromGid(variant?.id);
        if (!variantId) return states;
        const enabled = hasAnyVariantConfig
          ? String(variant.preorderItem?.value || '').toLowerCase() === 'true'
          : preorderEnabled;
        const timeline = hasAnyVariantConfig
          ? richTextToPlainText(variant.preorderTimeline?.value || '')
          : preorderTimeline;
        states[variantId] = { active: enabled && !!timeline, timeline };
        return states;
      }, {})
      : {};
    const bundleProduct = product.virtualBundleProduct?.reference;
    const bundleVariants = Array.isArray(bundleProduct?.variants?.nodes)
      ? bundleProduct.variants.nodes.map((variant) => {
        const selectedOptions = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
        return {
          id: numericIdFromGid(variant?.id),
          available: !!variant?.availableForSale,
          option1: selectedOptions[0]?.value || null,
          option2: selectedOptions[1]?.value || null,
          option3: selectedOptions[2]?.value || null,
        };
      }).filter((variant) => variant.id)
      : [];
    const bundleHasAnyVariantConfig = Array.isArray(bundleProduct?.variants?.nodes)
      && bundleProduct.variants.nodes.some((variant) => variant?.preorderItem != null || variant?.preorderTimeline != null);
    const virtualBundlePreorderStates = Array.isArray(bundleProduct?.variants?.nodes)
      ? bundleProduct.variants.nodes.reduce((states, variant) => {
        const variantId = numericIdFromGid(variant?.id);
        if (!variantId) return states;
        const timeline = richTextToPlainText(variant?.preorderTimeline?.value || '');
        states[variantId] = {
          active: bundleHasAnyVariantConfig
            ? String(variant?.preorderItem?.value || '').toLowerCase() === 'true' && !!timeline
            : false,
          timeline: bundleHasAnyVariantConfig ? timeline : '',
        };
        return states;
      }, {})
      : {};

    return {
      handle: String(product.handle || ''),
      customPreorderActive: hasAnyVariantConfig
        ? false
        : preorderEnabled && !!preorderTimeline,
      preorderTimeline: hasAnyVariantConfig ? '' : preorderTimeline,
      variantPreorderStates,
      virtualBundleQuantity: Math.max(1, Number(product.virtualBundleQuantity?.value) || 1),
      virtualBundleVariants: bundleVariants,
      virtualBundlePreorderStates,
    };
  }

  async function enrichHitsWithShopifyMetadata(hits) {
    if (!Array.isArray(hits) || !hits.length) return hits || [];

    const uncachedIds = [];
    hits.forEach((hit) => {
      const gid = toProductGid(hit?.id);
      if (gid && !productMetadataCache.has(gid)) uncachedIds.push(gid);
    });

    const uniqueIds = [...new Set(uncachedIds)];
    const storefrontToken = getStorefrontAccessToken();

    if (uniqueIds.length && storefrontToken) {
      try {
        const response = await fetch('/api/2025-10/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': storefrontToken,
          },
          body: JSON.stringify({
            query: `
              query SearchCardMetadata($ids: [ID!]!) {
                nodes(ids: $ids) {
                  ... on Product {
                    id
                    handle
                    preorderItem: metafield(namespace: "custom", key: "pre_order_item") { value }
                    preorderTimeline: metafield(namespace: "custom", key: "pre_order_timeline") { value }
                    variants(first: 100) {
                      nodes {
                        id
                        preorderItem: metafield(namespace: "custom", key: "pre_order_item") { value }
                        preorderTimeline: metafield(namespace: "custom", key: "pre_order_timeline") { value }
                      }
                    }
                    virtualBundleQuantity: metafield(namespace: "custom", key: "virtual_bundle_quantity") { value }
                    virtualBundleProduct: metafield(namespace: "custom", key: "virtual_bundle_single_product") {
                      reference {
                        ... on Product {
                          variants(first: 100) {
                            nodes {
                              id
                              availableForSale
                              selectedOptions { value }
                              preorderItem: metafield(namespace: "custom", key: "pre_order_item") { value }
                              preorderTimeline: metafield(namespace: "custom", key: "pre_order_timeline") { value }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            `,
            variables: { ids: uniqueIds },
          }),
        });

        if (response.ok) {
          const payload = await response.json();
          if (Array.isArray(payload?.errors) && payload.errors.length) {
            throw new Error(payload.errors.map((error) => error.message).filter(Boolean).join('; '));
          }
          const products = Array.isArray(payload?.data?.nodes) ? payload.data.nodes : [];
          products.forEach((product) => {
            if (product?.id) productMetadataCache.set(product.id, normalizeMetadataProduct(product));
          });
          uniqueIds.forEach((gid) => {
            if (!productMetadataCache.has(gid)) productMetadataCache.set(gid, null);
          });
        }
      } catch (error) {
        console.warn('[EG Search] Unable to load product card metadata:', error);
      }
    }

    return hits.map((hit) => ({
      ...hit,
      epic_card_metadata: productMetadataCache.get(toProductGid(hit?.id)) || null,
    }));
  }

  function buildFilterString(filters) {
    const filterParts = [];

    // Always exclude specific product types at query-time (keeps pagination/counts correct).
    EXCLUDED_PRODUCT_TYPES.forEach((pt) => {
      const safe = String(pt).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
      filterParts.push(`product_type != '${safe}'`);
    });

    for (const [key, values] of Object.entries(filters || {})) {
      // Price range is a special case (gte/lte).
      if (key === '_priceRange' && typeof values === 'object') {
        const priceConditions = [];
        if (values.gte !== undefined) priceConditions.push(`price >= ${values.gte}`);
        if (values.lte !== undefined) priceConditions.push(`price <= ${values.lte}`);
        if (priceConditions.length) {
          filterParts.push(`(${priceConditions.join(' AND ')})`);
        }
        continue;
      }

      // Array-based filters (product_type, etc.)
      if (Array.isArray(values) && values.length) {
        const conditions = values.map(v => {
          if (typeof v === 'boolean') return `${key} = ${v}`;
          const num = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : NaN);
          if (Number.isFinite(num) && String(v).trim() !== '') return `${key} = ${num}`;
          // Use single quotes so values like `15"` don't cause headaches.
          const safe = String(v).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
          return `${key} = '${safe}'`;
        }).join(' OR ');
        filterParts.push(`(${conditions})`);
      }
    }

    const result = filterParts.join(' AND ');
    // if (result) console.log('Meilisearch filter string:', result);
    return result;
  }

  function buildSearchParams({ query, page, hitsPerPage = HITS_PER_PAGE, facets = FACETS_LIST, filters = {}, sort = '' }) {
    const params = new URLSearchParams({
      q: query || '',
      page: String(page || 0),
      hitsPerPage: String(hitsPerPage),
      facets: String(facets),
    });

    const filterString = buildFilterString(filters);
    if (filterString) params.set('filters', filterString);
    if (sort) params.set('sort', sort);

    return params;
  }

  async function fetchSearchPage({ query, page, hitsPerPage = HITS_PER_PAGE, facets = FACETS_LIST, filters = {}, sort = '', onHits }) {
    const params = buildSearchParams({ query, page, hitsPerPage, facets, filters, sort });
    const url = `${config.API_BASE}/search?${params}`;
    const hint = document.getElementById('eg-search-request-preload');
    const preload = hint?.href === url ? hint : null;
    let res;
    try {
      res = await fetch(url);
    } catch (error) {
      if (!preload) throw error;
    }
    if (preload) {
      preload.remove();
      // A failed speculative request must not poison the user's actual search.
      if (!res?.ok) res = await fetch(url);
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    // Resource hints may start before commerce metadata, but cannot bypass it.
    try {
      onHits?.(data.hits || []);
    } catch (error) {
      console.warn('[EG Search] Unable to prepare result resource hints:', error);
    }
    data.hits = await enrichHitsWithShopifyMetadata(data.hits || []);
    return data;
  }

  return {
    FACETS_LIST,
    HITS_PER_PAGE,
    buildFilterString,
    buildSearchParams,
    fetchSearchPage,
  };
})();
